import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { navLinks, companyInfo } from "../data/mockData";
import { countryNavItems } from "../data/countryData";
import {
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  ChevronUp,
  ChevronDown,
  Globe,
  ArrowRight,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [countriesOpen, setCountriesOpen] = useState(false);
  const [mobileCountriesOpen, setMobileCountriesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <>
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-2 px-4 text-sm hidden md:block">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a
              href={`mailto:${companyInfo.email}`}
              className="flex items-center gap-2 hover:text-orange-400 transition-colors"
            >
              <Mail size={14} />
              {companyInfo.email}
            </a>
            <a
              href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}
              className="flex items-center gap-2 hover:text-orange-400 transition-colors"
            >
              <Phone size={14} />
              {companyInfo.phone}
            </a>
          </div>
          <div className="flex items-center gap-4">
            <a href="#" className="hover:text-orange-400 transition-colors"><Facebook size={14} /></a>
            <a href="#" className="hover:text-orange-400 transition-colors"><Twitter size={14} /></a>
            <a href="#" className="hover:text-orange-400 transition-colors"><Instagram size={14} /></a>
            <a href="#" className="hover:text-orange-400 transition-colors"><Linkedin size={14} /></a>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-md shadow-lg py-2"
            : "bg-white py-3"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
  <img
    src="/Sunlumia_logo.jpeg"
    alt="Sun Lumia Overseas"
    className="h-14 w-auto object-contain"
  />
</Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 relative ${
                  location.pathname === link.path
                    ? "text-orange-500"
                    : "text-slate-700 hover:text-orange-500"
                }`}
              >
                {link.name}
                {location.pathname === link.path && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute bottom-0 left-2 right-2 h-0.5 bg-orange-500 rounded-full"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </Link>
            ))}

            {/* Countries Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setCountriesOpen(true)}
              onMouseLeave={() => setCountriesOpen(false)}
            >
              <button
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-1 relative ${
                  location.pathname.startsWith("/country")
                    ? "text-orange-500"
                    : "text-slate-700 hover:text-orange-500"
                }`}
              >
                Countries
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-300 ${countriesOpen ? "rotate-180" : ""}`}
                />
                {location.pathname.startsWith("/country") && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute bottom-0 left-2 right-2 h-0.5 bg-orange-500 rounded-full"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </button>

              <AnimatePresence>
                {countriesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-1 w-56 bg-white rounded-xl shadow-xl border border-slate-100 py-2 z-50"
                  >
                    {countryNavItems.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/country/${c.slug}`}
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-orange-50 ${
                          location.pathname === `/country/${c.slug}`
                            ? "text-orange-500 bg-orange-50/50"
                            : "text-slate-700"
                        }`}
                      >
                        <img src={c.flag} alt={c.name} className="w-6 h-4 rounded object-cover shadow-sm" />
                        <span className="font-medium">{c.name}</span>
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/contact"
              className="ml-4 px-5 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/25 hover:-translate-y-0.5 flex items-center gap-2"
            >
              Get Free Counselling
              <ArrowRight size={14} />
            </Link>
          </nav>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 text-slate-700"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t overflow-hidden"
            >
              <div className="px-4 py-4 space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname === link.path
                        ? "bg-orange-50 text-orange-500"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}

                {/* Mobile Countries Accordion */}
                <div>
                  <button
                    onClick={() => setMobileCountriesOpen(!mobileCountriesOpen)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname.startsWith("/country")
                        ? "bg-orange-50 text-orange-500"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    Countries
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-300 ${mobileCountriesOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  <AnimatePresence>
                    {mobileCountriesOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="pl-4 space-y-0.5 py-1">
                          {countryNavItems.map((c) => (
                            <Link
                              key={c.slug}
                              to={`/country/${c.slug}`}
                              className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${
                                location.pathname === `/country/${c.slug}`
                                  ? "bg-orange-50 text-orange-500"
                                  : "text-slate-600 hover:bg-slate-50"
                              }`}
                            >
                              <img src={c.flag} alt={c.name} className="w-5 h-3.5 rounded object-cover shadow-sm" />
                              {c.name}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <Link
                  to="/contact"
                  className="block mt-3 px-4 py-3 bg-orange-500 text-white rounded-lg text-sm font-medium text-center"
                >
                  Get Free Counselling
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

const Footer = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <footer className="bg-slate-900 text-white">
        {/* Main Footer */}
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* Company Info */}
            <div>
              <div className="mb-4">
  <img
    src="/Sunlumia_logo.jpeg"
    alt="Sun Lumia Overseas"
    className="h-12 w-auto object-contain"
  />
</div>
              <p className="text-slate-400 text-sm leading-relaxed mb-4">
                One of the largest education consultancies in India, representing
                over 150 education institutions globally since 2010.
              </p>
              
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-orange-400">
                Quick Links
              </h4>
              <ul className="space-y-3">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-slate-400 text-sm hover:text-orange-400 transition-colors flex items-center gap-2"
                    >
                      <ArrowRight size={12} />
                      {link.name}
                    </Link>
                  </li>
                ))}
                <li>
                  <a href="#" className="text-slate-400 text-sm hover:text-orange-400 transition-colors flex items-center gap-2">
                    <ArrowRight size={12} />
                    Visa Services
                  </a>
                </li>
              </ul>
            </div>

            {/* Countries */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-orange-400">
                Countries We Serve
              </h4>
              <ul className="space-y-3">
                {[
                  { name: "United States", slug: "usa" },
                  { name: "Canada", slug: "canada" },
                  { name: "Australia", slug: "australia" },
                  { name: "Germany", slug: "germany" },
                  { name: "Italy", slug: "italy" },
                ].map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/country/${c.slug}`}
                      className="text-slate-400 text-sm hover:text-orange-400 transition-colors flex items-center gap-2"
                    >
                      <ArrowRight size={12} />
                      {c.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-orange-400">
                Contact Us
              </h4>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="text-orange-500 mt-0.5 flex-shrink-0" />
                  <p className="text-slate-400 text-sm">{companyInfo.address}</p>
                </div>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-3 text-slate-400 text-sm hover:text-orange-400 transition-colors"
                >
                  <Mail size={18} className="text-orange-500 flex-shrink-0" />
                  {companyInfo.email}
                </a>
                <a
                  href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}
                  className="flex items-center gap-3 text-slate-400 text-sm hover:text-orange-400 transition-colors"
                >
                  <Phone size={18} className="text-orange-500 flex-shrink-0" />
                  {companyInfo.phone}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-4 py-5 flex flex-col md:flex-row justify-between items-center gap-2">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Sun Lumia Overseas. All rights reserved.
            </p>
          
          </div>
        </div>
      </footer>

      {/* Scroll to Top */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="fixed bottom-6 right-6 w-11 h-11 bg-orange-500 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-orange-600 transition-colors z-50"
          >
            <ChevronUp size={20} />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};

const Layout = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

export default Layout;
